// [STORE-CLICK] Full report page for App Store + Google Play download button analytics
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { TimeRangeValue, resolveRangeYmd } from '../common/timeRange';
import { safeMetric } from '../../utils/safeMetric';

/**
 * This endpoint family speaks numeric-string day counts (`range=7`), not the
 * canonical `7d` tokens. `all` / `custom` are the two non-numeric values it
 * takes; an arbitrary span now arrives as `custom` + `start`/`end`, which is
 * also how the old "All Time" option is reached.
 */
const RANGE_TOKENS: Record<string, string> = {
  today: 'today',
  '3d': '3',
  '7d': '7',
  '14d': '14',
  '30d': '30',
  '90d': '90',
};

/** `range=…` plus `start`&`end` when the operator picked a custom span. */
function storeClickQuery(value: TimeRangeValue): string {
  if (value.type === 'custom') {
    const { from, to } = resolveRangeYmd(value);
    return `range=custom&start=${from}&end=${to}`;
  }
  return `range=${RANGE_TOKENS[value.type] ?? '30'}`;
}

const IOS_COLOR = '#1A5F7A';
const ANDROID_COLOR = '#D4AC0D';

interface SummaryData {
  total: number;
  ios: number;
  android: number;
  ios_pct: number;
  android_pct: number;
  change_vs_prior_period_pct: number | null;
}

interface TimeseriesRow {
  bucket: string;
  ios: number;
  android: number;
  total: number;
}

interface UtmRow {
  utm_campaign: string;
  utm_source: string;
  utm_medium: string;
  ios: number;
  android: number;
  total: number;
}

interface RecentRow {
  id: string;
  button_type: string;
  clicked_at: string;
  utm_campaign: string | null;
  country_code: string | null;
  device_type: string | null;
  os_family: string | null;
  browser_family: string | null;
}

/** A rejected section's message; null when the section loaded. */
type SectionErr = string | null;

const rejectionMessage = (r: PromiseSettledResult<unknown>): SectionErr => {
  if (r.status !== 'rejected') return null;
  const reason: unknown = r.reason;
  return reason instanceof Error ? reason.message : String(reason ?? 'Request failed');
};

/**
 * Guarded date formatting. `new Date(x).toLocaleDateString()` does NOT throw on
 * a bad value — it returns the literal string "Invalid Date", which is how a
 * malformed bucket used to end up on the x-axis and in the recent-clicks table.
 */
const fmtBucket = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmtClickedAt = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Inline "this section failed" panel with its own retry. */
const SectionErrorPanel: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <Box
    sx={{
      border: '1px solid #FCA5A5',
      bgcolor: '#FEF2F2',
      borderRadius: '12px',
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: 'wrap',
    }}
  >
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#B0202F' }}>
      Couldn&apos;t load this section — {message}. These figures are unknown, not zero.
    </Typography>
    <Button variant="outlined" color="error" size="small" onClick={onRetry}
      sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 600 }}>
      Retry
    </Button>
  </Box>
);

const StatTile: React.FC<{
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
  loading: boolean;
}> = ({ label, value, subtitle, color, loading }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: '14px',
      border: '1px solid #e5e7eb',
      borderTop: `3px solid ${color}`,
      p: '18px 16px',
      flex: 1,
    }}
  >
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#6B7280', mb: 0.5 }}>
      {label}
    </Typography>
    {loading ? (
      <Skeleton variant="text" width={60} height={32} />
    ) : (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '26px', fontWeight: 800, color: '#111827', lineHeight: 1.2, mb: 0.3 }}>
        {safeMetric(value)}
      </Typography>
    )}
    {subtitle && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 500, color: '#6B7280' }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StoreClickAnalyticsPage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '30d' });
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesRow[]>([]);
  const [utmBreakdown, setUtmBreakdown] = useState<UtmRow[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingUtm, setLoadingUtm] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  // Promise.allSettled used to swallow every rejection, leaving the previous
  // (or initial) state in place — so a total API outage rendered a tidy page of
  // zeros. Each section now remembers its own failure and says so.
  const [summaryError, setSummaryError] = useState<SectionErr>(null);
  const [chartError, setChartError] = useState<SectionErr>(null);
  const [utmError, setUtmError] = useState<SectionErr>(null);
  const [recentError, setRecentError] = useState<SectionErr>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const loadAll = useCallback(async (value: TimeRangeValue) => {
    const q = storeClickQuery(value);
    setLoadingSummary(true);
    setLoadingChart(true);
    setLoadingUtm(true);
    setLoadingRecent(true);
    setSummaryError(null);
    setChartError(null);
    setUtmError(null);
    setRecentError(null);

    const [sumRes, tsRes, utmRes, recRes] = await Promise.allSettled([
      apiService.get(`/api/admin/store-clicks/summary?${q}`),
      apiService.get(`/api/admin/store-clicks/timeseries?${q}&granularity=day`),
      apiService.get(`/api/admin/store-clicks/utm-breakdown?${q}&limit=20`),
      apiService.get(`/api/admin/store-clicks/recent?limit=100`),
    ]);

    if (sumRes.status === 'fulfilled') {
      setSummary((sumRes.value.data as SummaryData) ?? null);
    } else {
      setSummary(null);
      setSummaryError(rejectionMessage(sumRes) ?? 'Request failed');
    }
    setLoadingSummary(false);

    if (tsRes.status === 'fulfilled') {
      const rows = ((tsRes.value.data as TimeseriesRow[]) ?? []).map((row) => ({
        ...row,
        label: fmtBucket(row.bucket),
      }));
      setTimeseries(rows as TimeseriesRow[]);
    } else {
      setTimeseries([]);
      setChartError(rejectionMessage(tsRes) ?? 'Request failed');
    }
    setLoadingChart(false);

    if (utmRes.status === 'fulfilled') {
      setUtmBreakdown((utmRes.value.data as UtmRow[]) ?? []);
    } else {
      setUtmBreakdown([]);
      setUtmError(rejectionMessage(utmRes) ?? 'Request failed');
    }
    setLoadingUtm(false);

    if (recRes.status === 'fulfilled') {
      setRecent((recRes.value.data as RecentRow[]) ?? []);
    } else {
      setRecent([]);
      setRecentError(rejectionMessage(recRes) ?? 'Request failed');
    }
    setLoadingRecent(false);
  }, []);

  useEffect(() => {
    loadAll(range);
  }, [range, loadAll, reloadKey]);

  const changeBadge = summary?.change_vs_prior_period_pct != null
    ? `${summary.change_vs_prior_period_pct >= 0 ? '+' : ''}${summary.change_vs_prior_period_pct}% vs prior`
    : null;

  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 700, color: '#111827' }}>
            Store Click Analytics
          </Typography>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#6B7280', mt: 0.5 }}>
            App Store + Google Play download button performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TimeRangeFilter value={range} onChange={setRange} onRefresh={retry} />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              // The export must carry the SAME window as the page, custom span
              // included — otherwise the CSV silently reports a different period.
              window.location.href = `/api/admin/store-clicks/export.csv?${storeClickQuery(range)}`;
            }}
            sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 600 }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Stat Strip — every tile reads from `summary`, so one failure takes the
          whole strip; a failed strip must not print five zeros. */}
      {summaryError ? (
        <Box sx={{ mb: 3 }}>
          <SectionErrorPanel message={summaryError} onRetry={retry} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: '14px', mb: 3, flexWrap: 'wrap' }}>
          <StatTile label="Total Clicks"    value={summary?.total ?? '—'}   color="#1A5F7A" loading={loadingSummary} />
          <StatTile label="iOS Clicks"      value={summary?.ios ?? '—'}     color="#1A5F7A" loading={loadingSummary} />
          <StatTile label="Android Clicks"  value={summary?.android ?? '—'} color="#D4AC0D" loading={loadingSummary} />
          <StatTile label="iOS Share"       value={summary ? `${summary.ios_pct}%` : '—'} color="#1A5F7A" loading={loadingSummary} />
          <StatTile
            label="Δ vs Prior Period"
            value={changeBadge ?? '—'}
            color="#10B981"
            loading={loadingSummary}
          />
        </Box>
      )}

      {/* Timeseries Chart */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 2 }}>
          Daily Clicks — iOS vs Android
        </Typography>
        {loadingChart ? (
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
        ) : chartError ? (
          <SectionErrorPanel message={chartError} onRetry={retry} />
        ) : timeseries.length === 0 ? (
          <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#9CA3AF' }}>
              No clicks recorded for this time range yet
            </Typography>
          </Box>
        ) : (
          <div style={{ height: 320, minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries as any[]}>
                <defs>
                  <linearGradient id="gradIos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={IOS_COLOR} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={IOS_COLOR} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradAndroid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ANDROID_COLOR} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={ANDROID_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }} />
                <Area type="monotone" dataKey="ios" name="iOS" stroke={IOS_COLOR} fill="url(#gradIos)" strokeWidth={2} />
                <Area type="monotone" dataKey="android" name="Android" stroke={ANDROID_COLOR} fill="url(#gradAndroid)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Paper>

      {/* UTM Campaign Breakdown */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 2 }}>
          UTM Campaign Breakdown
        </Typography>
        {loadingUtm ? (
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ) : utmError ? (
          <SectionErrorPanel message={utmError} onRetry={retry} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Campaign', 'Source', 'Medium', 'iOS', 'Android', 'Total'].map((h) => (
                    <TableCell key={h} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {utmBreakdown.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#9CA3AF', py: 3 }}>
                      No data for this range
                    </TableCell>
                  </TableRow>
                ) : utmBreakdown.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px' }}>{row.utm_campaign}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px' }}>{row.utm_source}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px' }}>{row.utm_medium}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: IOS_COLOR, fontWeight: 600 }}>{row.ios}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: ANDROID_COLOR, fontWeight: 600 }}>{row.android}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700 }}>{row.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Recent Clicks */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 2 }}>
          Recent Clicks (last 100)
        </Typography>
        {loadingRecent ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : recentError ? (
          <SectionErrorPanel message={recentError} onRetry={retry} />
        ) : (
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Time', 'Button', 'Campaign', 'Country', 'OS', 'Browser', 'Device'].map((h) => (
                    <TableCell key={h} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', bgcolor: '#F9FAFB' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#9CA3AF', py: 3 }}>
                      No recent clicks
                    </TableCell>
                  </TableRow>
                ) : recent.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {fmtClickedAt(row.clicked_at)}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 700, color: row.button_type === 'ios' ? IOS_COLOR : ANDROID_COLOR }}>
                        {row.button_type === 'ios' ? 'iOS' : 'Android'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>{row.utm_campaign ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>{row.country_code ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>{row.os_family ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>{row.browser_family ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>{row.device_type ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default StoreClickAnalyticsPage;
